import { Proposal, Group } from '../types';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { api, getErrorMessage, handleServiceError } from './api';
import * as Sentry from '@sentry/react';

// ====================================================================
// TIPOS ADICIONAIS PARA BACKEND
// ====================================================================

export interface ProposalCreate {
  title: string;
  type: string;
  active?: boolean;
  group_id?: number | null;
}

export interface ProposalUpdate {
  title?: string;
  type?: string;
  active?: boolean;
  group_id?: number | null;
}

export interface VectorStoreStatus {
  status: string;
  vector_store_id?: string;
  has_vector_store: boolean;
  total_documents?: number;
  indexed_documents?: number;
  pending_documents?: number;
  error?: string;
}

// ====================================================================
// PROPOSAL SERVICE
// ====================================================================

export const proposalService = {
  /**
   * Busca todas as propostas do usuário logado
   */
  getAll: async (includeArchived: boolean = false): Promise<Proposal[]> => {
    try {
      console.log('📡 Chamando API de propostas - includeArchived:', includeArchived);
      const response = await api.get(API_ENDPOINTS.PROPOSALS.LIST, {
        params: { 
          include_archived: includeArchived
        },
      });
      console.log('📥 Resposta da API de propostas:', response.data);
      return response?.data || [];
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'get_all' });
    }
  },

  /**
   * Busca propostas do usuário (próprias + compartilhadas)
   * Endpoint /proposals/ retorna: propostas criadas pelo usuário + propostas compartilhadas via ProposalPermission
   */
  getOrganizationProposals: async (includeArchived: boolean = false): Promise<Proposal[]> => {
    try {
      console.log('� Buscando propostas - includeArchived:', includeArchived);
      const response = await api.get('/proposals/', {
        params: { 
          include_archived: includeArchived,
          skip: 0,
          limit: 100
        }
      });
      const proposals = response?.data || [];
      
      console.log('📥 Propostas recebidas:', proposals.length);
      console.log('🔢 IDs:', proposals.map((p: Proposal) => p.id));
      
      return proposals;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'get_organization_proposals' });
    }
  },

  /**
   * Busca uma proposta específica por ID
   */
  getById: async (id: number): Promise<Proposal> => {
    try {
      console.log(`[proposalService.getById] Buscando proposta ID: ${id}`);
      console.log(`[proposalService.getById] Endpoint: ${API_ENDPOINTS.PROPOSALS.DETAILS(id)}`);
      
      const response = await api.get(API_ENDPOINTS.PROPOSALS.DETAILS(id));
      
      console.log(`[proposalService.getById] Resposta recebida:`, response?.data);
      return response?.data;
    } catch (error) {
      console.error(`[proposalService.getById] Erro ao buscar proposta ${id}:`, error);
      handleServiceError(error, { feature: 'proposals', action: 'get_by_id', proposal_id: id });
    }
  },

  /**
   * Cria uma nova proposta
   */
  create: async (data: Partial<Proposal>): Promise<Proposal> => {
    try {
      const payload: ProposalCreate = {
        title: data.title || '',
        type: data.type || 'Outro',
        active: data.active ?? true,
        group_id: data.group_id || null,
      };

      console.log('🔧 Criando proposta com payload:', payload);
      const response = await api.post(API_ENDPOINTS.PROPOSALS.CREATE, payload, {
        headers: { 'X-Skip-Error-Toast': 'true' },
      });
      console.log('✅ Proposta criada com sucesso:', response.data);
      
      // Verificar se a proposta realmente foi criada tentando buscar individualmente
      if (response.data?.id) {
        try {
          console.log('🔍 Verificando se proposta ID', response.data.id, 'realmente existe...');
          const verifyResponse = await api.get(`/proposals/${response.data.id}`);
          console.log('✅ Verificação bem-sucedida:', verifyResponse.data);
          return response.data;
        } catch (verifyError) {
          console.error('❌ ERRO CRÍTICO: Proposta não foi realmente criada no banco:', verifyError);
          
          // Capturar erro no Sentry com contexto completo
          Sentry.captureException(verifyError, {
            tags: {
              feature: 'proposal_verification',
              proposal_id: response.data.id,
            },
            extra: {
              proposal_data: response.data,
              verification_url: `/proposals/${response.data.id}`,
              error_details: verifyError,
            },
            level: 'error',
          });
          
          throw new Error('Proposta foi criada mas não encontrada no banco de dados. Tente novamente.');
        }
      }
      
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'create' });
    }
  },

  /**
   * Atualiza uma proposta existente
   */
  update: async (id: number, data: Partial<Proposal>): Promise<Proposal> => {
    try {
      const payload: ProposalUpdate = {};

      if (data.title !== undefined) payload.title = data.title;
      if (data.type !== undefined) payload.type = data.type;
      if (data.active !== undefined) payload.active = data.active;
      if (data.group_id !== undefined) payload.group_id = data.group_id;

      const response = await api.patch(API_ENDPOINTS.PROPOSALS.UPDATE(id), payload);
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'update', proposal_id: id });
    }
  },

  /**
   * Deleta uma proposta
   */
  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(API_ENDPOINTS.PROPOSALS.DELETE(id));
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'delete', proposal_id: id });
    }
  },

  /**
   * Arquiva uma proposta
   */
  archive: async (id: number): Promise<Proposal> => {
    try {
      console.log(`🔧 Arquivando proposta ${id}...`);
      const response = await api.patch(`/proposals/${id}/archive`);
      console.log(`✅ Proposta ${id} arquivada com sucesso:`, response.data);
      return response?.data;
    } catch (error: any) {
      console.error(`❌ Erro ao arquivar proposta ${id}:`, error);
      
      // Se for 500 ou 404, verificar se a proposta ainda existe
      if (error?.response?.status === 500 || error?.response?.status === 404) {
        console.log(`🔍 Verificando se proposta ${id} ainda existe...`);
        try {
          await this.getById(id);
          console.log(`❌ Proposta ${id} existe mas falhou ao arquivar (erro do backend)`);
          throw new Error(`Erro do servidor ao arquivar proposta ${id}. Tente novamente.`);
        } catch (notFoundError: any) {
          if (notFoundError?.response?.status === 404) {
            console.log(`🚨 Proposta ${id} não existe mais!`);
            throw new Error(`Proposta ${id} não encontrada. Pode ter sido deletada.`);
          }
          throw notFoundError;
        }
      }
      
      handleServiceError(error, { feature: 'proposals', action: 'archive', proposal_id: id });
    }
  },

  /**
   * Desarquiva uma proposta
   */
  unarchive: async (id: number): Promise<Proposal> => {
    try {
      const response = await api.patch(`/proposals/${id}/unarchive`);
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'unarchive', proposal_id: id });
    }
  },

  /**
   * Alterna o estado de fixado/não fixado de uma proposta
   */
  togglePin: async (id: number): Promise<Proposal> => {
    try {
      const response = await api.patch(`/proposals/${id}/toggle-pin`);
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'toggle_pin', proposal_id: id });
    }
  },

  /**
   * Define se a proposta usará ou não EAP (Estrutura Analítica de Projeto)
   * IMPORTANTE: Uma vez desativada, não pode ser reativada
   */
  setEapUsage: async (id: number, useEap: boolean): Promise<Proposal> => {
    try {
      const response = await api.patch(`/proposals/${id}/set-eap-usage`, {
        use_eap: useEap,
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'set_eap_usage', proposal_id: id });
    }
  },

  /**
   * Exporta uma proposta como PDF
   */
  exportPdf: async (id: number): Promise<Blob> => {
    try {
      const response = await api.get(API_ENDPOINTS.PROPOSALS.EXPORT_PDF(id), {
        responseType: 'blob',
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'export_pdf', proposal_id: id });
    }
  },

  /**
   * Exporta uma proposta como DOCX
   */
  exportDocx: async (id: number): Promise<Blob> => {
    try {
      const response = await api.get(API_ENDPOINTS.PROPOSALS.EXPORT_DOCX(id), {
        responseType: 'blob',
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'export_docx', proposal_id: id });
    }
  },

  /**
   * Retorna o status da geração assíncrona registrado no Redis
   */
  getGenerationStatus: async (id: number): Promise<{ status: 'running' | 'completed' | 'failed' | 'idle'; started_at?: string; error?: string } | null> => {
    try {
      const response = await api.get(API_ENDPOINTS.PROPOSALS.GENERATION_STATUS(id), {
        headers: { 'X-Skip-Error-Toast': 'true' },
      });
      return response?.data ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Re-gera todo o conteúdo de IA de uma proposta
   * (Instruções, Contexto e Conteúdo Final)
   */
  regenerateWithAI: async (id: number, body?: { reasoning_level?: string; custom_instructions?: string | null; ephemeral_index_id?: string | null }): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.PROPOSALS.REGENERATE(id), body ?? {}, {
        timeout: 600000, // 10 minutos para operação batch de IA
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'regenerate_ai', proposal_id: id });
    }
  },

  /**
   * Melhora com IA todas as seções da Elaboração que já possuem conteúdo (pula vazias).
   * Mesmo padrão em background + polling do regenerateWithAI.
   */
  improveAll: async (id: number, body?: { reasoning_level?: string; custom_instructions?: string | null; ephemeral_index_id?: string | null }): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.PROPOSALS.IMPROVE_ALL(id), body ?? {}, {
        timeout: 600000,
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'improve_all', proposal_id: id });
    }
  },

  /**
   * Envia um documento de apoio EFÊMERO: indexado só para a próxima geração/melhoria
   * e apagado depois. Devolve o ephemeral_index_id a passar no body de regenerate/improve.
   */
  uploadEphemeralDocument: async (id: number, file: File): Promise<string | null> => {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post(API_ENDPOINTS.PROPOSALS.EPHEMERAL_DOCUMENT(id), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return response?.data?.ephemeral_index_id ?? null;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'ephemeral_document', proposal_id: id });
      return null;
    }
  },

  /**
   * Importa o conteúdo de uma proposta externa (PDF/DOCX) e usa IA para distribuí-lo
   * nas seções JÁ EXISTENTES do roteiro desta proposta, adaptando ao limite e à
   * instrução de cada seção. Não cria proposta nova.
   *
   * As seções que casarem têm o conteúdo SOBRESCRITO — o histórico é preservado
   * (operation "import_adapt") e a mudança é revertível pela aba de Versões.
   */
  importContentIntoRoadmap: async (
    proposalId: number,
    file: File,
  ): Promise<{
    filled: number[];
    skipped: number[];
    filled_sections: { position: number; topic: string }[];
    skipped_sections: { position: number; topic: string }[];
    truncated: boolean;
    original_chars: number;
    used_chars: number;
    chunked: boolean;
    chunks: number;
  }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(API_ENDPOINTS.PROPOSALS.IMPORT_CONTENT(proposalId), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    return response.data;
  },

  /**
   * Busca as instruções específicas salvas para geração ('generate') ou melhoria ('improve')
   * em lote de todas as seções da Elaboração.
   */
  getElaborationInstructions: async (id: number, type: 'generate' | 'improve'): Promise<string | null> => {
    try {
      const response = await api.get(API_ENDPOINTS.PROPOSALS.ELABORATION_INSTRUCTIONS(id), {
        params: { type },
      });
      return response?.data?.custom_instructions ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Configura o Vector Store para uma proposta
   * Cria o Vector Store (se não existir) e indexa todos os documentos
   */
  setupVectorStore: async (id: number): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.PROPOSALS.SETUP_VECTOR_STORE(id));
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'setup_vector_store', proposal_id: id });
    }
  },

  /**
   * Verifica o status detalhado do Vector Store de uma proposta
   */
  getVectorStoreStatus: async (id: number): Promise<VectorStoreStatus> => {
    try {
      const response = await api.get(API_ENDPOINTS.PROPOSALS.VECTOR_STORE_STATUS(id));
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'get_vector_store_status', proposal_id: id });
    }
  },

  // ====================================================================
  // HELPERS DE DOWNLOAD
  // ====================================================================

  /**
   * Faz download de um arquivo PDF
   */
  downloadPdf: async (id: number, filename?: string): Promise<void> => {
    try {
      const blob = await proposalService.exportPdf(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `proposta_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'download_pdf', proposal_id: id });
    }
  },

  /**
   * Faz download de um arquivo DOCX
   */
  downloadDocx: async (id: number, filename?: string): Promise<void> => {
    try {
      const blob = await proposalService.exportDocx(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `proposta_${id}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleServiceError(error, { feature: 'proposals', action: 'download_docx', proposal_id: id });
    }
  },
};

// ====================================================================
// GROUP SERVICE
// ====================================================================

export const groupService = {
  getAll: async (): Promise<Group[]> => {
    try {
      const response = await api.get(API_ENDPOINTS.GROUPS.LIST);
      return response?.data || [];
    } catch (error) {
      handleServiceError(error, { feature: 'groups', action: 'get_all' });
    }
  },

  create: async (name: string): Promise<Group> => {
    try {
      const response = await api.post(API_ENDPOINTS.GROUPS.CREATE, { name });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'groups', action: 'create' });
    }
  },

  update: async (id: number, name: string): Promise<Group> => {
    try {
      const response = await api.patch(API_ENDPOINTS.GROUPS.UPDATE(id), { name });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'groups', action: 'update' });
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(API_ENDPOINTS.GROUPS.DELETE(id));
    } catch (error) {
      handleServiceError(error, { feature: 'groups', action: 'delete' });
    }
  },
};
